/*
  Recommendation rows (REC-THEME-1) – snippets/rec-row.liquid
  Contract v1: Documentation/Marketing/REC_ENGINE_DESIGN_2026-10-14.md §Kontrakt (REC-ENGINE-1).
  - Asks the engine: GET <endpoint>?surface=&market=&client=&product=&cart=&wl=&rv=&limit=&collection=
    surface = pdp (For you) | pdp_complement (Pairs well with) | cart | collection.
    product, cart, wl and rv are product ids – handles are translated per language.
    collection = the collection's id, only on the collection surface: the engine then
    picks only products from that collection (REC-COLLECTION-SCOPE-1).
    plating = the collection's metal (metafield custom.plating, metaobject handle), only with
    collection: the engine answers with the variant in that metal (url ?variant=); the row then
    shows exactly that variant and drops the product if it is no longer available (TILLÄGG 2/3).
  - placement (collection pages): 'above' = where the section sits; 'row_N' = moved into the
    product grid after N rows – N × the grid's columns (counted from the rendered grid, so 2 on
    mobile and the section's 4 on desktop), full width, re-placed when the width changes.
    In the grid the row uses the GRID'S OWN product card (REC-COLLECTION-GRID-ONE-RULE TILLÄGG 2): each tile is
    sections/rec-grid-item.liquid (snippets/product-grid-item.liquid) fetched within the collection
    (<collection url>/products/<handle>?variant=<engine's variant>&section_id=rec-grid-item), given the grid's own item
    classes, and exactly one line of them is shown (= the grid's columns: 2 on mobile, per_row on desktop). Above the
    cards an optional quiet row heading (section-header typography). 'above' keeps this row's own cards.
    Texts: heading / subtitle from the section or the collection (custom.rec_row_title / _subtitle / _show_subtitle,
    sections/rec-row.liquid).
    Answer: { personal, holdout, items: [{ slot, product_id, handle, variant_id, step, code }] }.
  - client = Shopify's clientId, the same id our web pixel sends as `cid`:
    window.ShopifyAnalytics.lib.user().traits().uniqToken (checked 2026-10-14 in
    Chrome against the live shop). Without analytics consent Shopify gives the
    placeholder 00000000-0000-0000-5000-000000000000; that is sent as no client.
  - rv = the wishlist app's own "Visited" list (localStorage timi_wl_visited, newest
    first), wl = the visitor's wishlist (/apps/wishlist/state, only when the visitor
    already has a wishlist key or is logged in). Personal rows also without clientId.
  - Title, price, image and stock are read from the product page under the
    visitor's locale root (sections/rec-product-json.liquid), so the row shows the
    shop's own language, the market's currency and only products in stock. The
    engine's source handles work on every domain (the page redirects them).
  - personalOnly: the row shows only when the answer is personal, or the visitor
    is in the holdout AND has a personal basis (answer.eligible – then the
    bestsellers are shown, code ends in _h). The query carries po=1 so the engine
    counts impressions only for rows that are shown (REC-FIX-1 G5).
  - replaceComplementary (PDP): when the engine has products, the theme's own
    complementary block (Search & Discovery, product-recommendations
    data-intent="complementary") is hidden so only one "Pairs well with" row
    shows; with no answer this row stays hidden and that block is the fallback.
  - Measuring (§K5): no utm_* on internal links. Every tile has data-rec-code (and
    data-rec-slot / data-rec-step / data-rec-product); a click publishes
    timi:rec_click {code, product_id}, an add timi:rec_add {code, product_id,
    variant_id} (Shopify.analytics.publish, for the pixel/collector), and the cart
    line gets the hidden property _rec = code.
  - Impressions (REC-BOTGUARD-1, 2026-10-16): the engine never counts a row when it answers. The query carries iv=1 and
    the answer to a row that counts carries a ticket (answer.iv). When the drawn row has been at least half on screen
    (half of the row, or half the window for a row taller than the window) for 500 ms while the tab is visible, the
    ticket goes ONCE to <endpoint>/impression (navigator.sendBeacon, text/plain JSON {iv, slots, view: 1, ms, wd: 0};
    slots = the cards actually drawn and not hidden) and timi:rec_view {surface, codes} is published. When
    navigator.webdriver is true (automated browser) the query carries wd=1 and nothing is sent or published – the row
    still shows. Clicks and adds are measured as before. Analysis: Documentation/Rekommendationer/
    BOTWAVE_2026-10-15_16_ANALYSIS.md §5.3.
  - excludeGrid (REC-COLLECTION-SORT-1, collection pages with the dynamic order on and a placement chosen by the collection):
    exclude=<the product ids on page 1> – the engine leaves them out, so the row and the sorted grid never show the same
    product twice near the top (assets/collection-sort.js).
  - endpoint 'mock:<collection handle>' answers from that collection's products in
    the browser (preview only, until REC-API is live).
  - layout 'page' (sections/rec-foryou.liquid, /pages/for-you – REC-FORYOU, contract
    Documentation/Rekommendationer/REC_FORYOU_PAGE_CONTRACT_2026-10-16.md): surface=foryou, lang, and t=<the e-mail's token>
    (read and taken out of the address bar by the section's inline script, kept only on the element as recToken). The
    standard cards fill a full grid (4 per row on desktop, 2 on mobile – the card's own classes), no carousel. Never empty:
    with the token -> the same call without it -> the collection cards the section drew in Liquid (data-rec-fallback);
    fewer than cfg.minTiles cards counts as nothing. A Quick view add from a rec card publishes timi:rec_add.
*/
(function () {
  if (window.customElements.get('rec-row')) return;

  var NO_CLIENT = '00000000-0000-0000-5000-000000000000';
  var TIMEOUT = 2500;
  var SURFACE_CODE = { pdp: 'pdp', pdp_complement: 'pdpc', cart: 'cart', collection: 'col', foryou: 'foryou' };
  var PERSONAL_STEPS = ['wl', 'rv', 'sim', 'vt', 'cpop'];
  var VIEW_MS = 500;
  var VIEW_SHARE = 0.5;

  // An automated browser (Selenium, Puppeteer, Playwright …) – its rows are never counted as seen (REC-BOTGUARD-1).
  function automated() {
    try { return navigator.webdriver === true; } catch (e) { return false; }
  }

  // <endpoint>/impression – the engine's view beacon; '' for the preview mock or an endpoint that is not …/rec.
  function impressionUrl(endpoint) {
    var e = String(endpoint || '');
    if (!e || e.indexOf('mock:') === 0) return '';
    var parts = e.split('?');
    var base = parts[0].replace(/\/+$/, '');
    if (!/\/rec$/.test(base)) return '';
    return base + '/impression' + (parts[1] ? '?' + parts[1] : '');
  }

  function sendBeacon(url, data) {
    var body = JSON.stringify(data);
    try {
      if (navigator.sendBeacon && navigator.sendBeacon(url, new Blob([body], { type: 'text/plain;charset=UTF-8' }))) return;
    } catch (e) { /* fall back to fetch */ }
    try {
      fetch(url, { method: 'POST', body: body, keepalive: true, credentials: 'omit', headers: { 'Content-Type': 'text/plain;charset=UTF-8' } })
        .catch(function () {});
    } catch (e) { /* measuring never breaks the row */ }
  }

  function root() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  }

  function money(cents) {
    if (window.theme && theme.Currency && theme.settings && theme.settings.moneyFormat) {
      return theme.Currency.formatMoney(cents, theme.settings.moneyFormat);
    }
    return (cents / 100).toFixed(2);
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function fill(text, title) {
    return String(text || '').replace('[title]', title);
  }

  // Shopify's clientId as the theme can read it, or '' (no consent / not loaded).
  function clientId() {
    try {
      var t = window.ShopifyAnalytics.lib.user().traits().uniqToken;
      return t && t !== NO_CLIENT ? String(t) : '';
    } catch (e) {
      return '';
    }
  }

  // The analytics library loads after the theme; wait up to 2 s for it.
  function whenClient() {
    return new Promise(function (resolve) {
      var tries = 0;
      (function poll() {
        var ready = window.ShopifyAnalytics && ShopifyAnalytics.lib && ShopifyAnalytics.lib.user;
        if (ready || tries++ >= 20) return resolve(clientId());
        setTimeout(poll, 100);
      })();
    });
  }

  // Product ids the visitor has looked at, newest first (wishlist app's Visited list).
  function visited() {
    try {
      var d = JSON.parse(localStorage.getItem('timi_wl_visited') || 'null');
      return (d && d.l || []).map(function (x) { return String(x.p || ''); }).filter(function (p) { return /^\d+$/.test(p); }).slice(0, 50);
    } catch (e) {
      return [];
    }
  }

  // Product ids in the visitor's wishlist; never creates a wishlist key.
  var wishlistPromise = null;
  function wishlist() {
    if (wishlistPromise) return wishlistPromise;
    var guest = null;
    try { guest = localStorage.getItem('timi_wl_guest'); } catch (e) {}
    var loggedIn = window.TimiWishlist && TimiWishlist.loggedIn;
    if (!guest && !loggedIn) return (wishlistPromise = Promise.resolve([]));
    wishlistPromise = fetch('/apps/wishlist/state' + (guest ? '?g=' + encodeURIComponent(guest) : ''), { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : {}; })
      .then(function (d) { return (d.product_ids || []).map(String).slice(0, 50); })
      .catch(function () { return []; });
    return wishlistPromise;
  }

  function getJSON(url, timeout) {
    var ctrl = window.AbortController ? new AbortController() : null;
    var timer = ctrl && setTimeout(function () { ctrl.abort(); }, timeout || TIMEOUT);
    return fetch(url, { credentials: 'omit', signal: ctrl ? ctrl.signal : undefined, headers: { Accept: 'application/json' } })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .finally(function () { if (timer) clearTimeout(timer); });
  }

  function storefront(path) {
    return fetch(root() + path, { headers: { Accept: 'application/json' } }).then(function (res) {
      if (!res.ok) throw new Error('HTTP ' + res.status);
      return res.json();
    });
  }

  // A product in the visitor's language and currency, by any of its handles:
  // the product page (sections/rec-product-json.liquid) follows the redirect from
  // the source handle to the translated one; /products/<handle>.js does not.
  var productCache = {};
  function productData(handle) {
    if (!productCache[handle]) {
      productCache[handle] = fetch(root() + 'products/' + encodeURIComponent(handle) + '?section_id=rec-product-json')
        .then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.text();
        })
        .then(function (html) {
          var m = html.match(/<script type="application\/json" data-rec-product>([\s\S]*?)<\/script>/);
          if (!m) throw new Error('no product data');
          return JSON.parse(m[1]);
        });
      productCache[handle].catch(function () { delete productCache[handle]; });
    }
    return productCache[handle];
  }

  function cartJSON() {
    return storefront('cart.js').catch(function () { return { items: [] }; });
  }

  function postJSON(path, body) {
    return fetch(root() + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body)
    }).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok || data.status) throw data;
        return data;
      });
    });
  }

  function publish(name, data) {
    try {
      if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) Shopify.analytics.publish(name, data);
    } catch (e) { /* measuring never breaks the row */ }
  }

  // One engine item in the contract's fields (§K2); `code` is built (§K5) when missing.
  function readItem(raw, i, surface, holdout) {
    var slot = Number(raw.slot) || i + 1;
    var step = String(raw.step || 'unk');
    return {
      slot: slot,
      step: step,
      productId: Number(raw.product_id) || null,
      handle: raw.handle || '',
      variant: Number(raw.variant_id || raw.variant) || null,
      exact: /[?&]variant=\d/.test(String(raw.url || '')),       // the engine chose this variant (plating)
      code: raw.code || ['rec', SURFACE_CODE[surface] || surface, slot, step, holdout ? 'h' : 'p'].join('_')
    };
  }

  // The grid's product items (not this row).
  function gridProducts(grid) {
    return Array.prototype.filter.call(grid.children, function (el) {
      return el.tagName !== 'REC-ROW' && /(^|\s)grid__item(\s|$)/.test(el.className);
    });
  }

  // Index of the product the row goes in front of: rows × columns, where columns = the products
  // on the first rendered line (2 on mobile, the section's per_row on desktop).
  function placementIndex(grid, rows) {
    var items = gridProducts(grid);
    if (!items.length) return 0;
    var top = items[0].offsetTop, cols = 0;
    for (var i = 0; i < items.length && items[i].offsetTop === top; i++) cols++;
    return Math.min(items.length, rows * Math.max(cols, 1));
  }

  // The collection's product grid (Impulse .grid--uniform), never a grid inside this row.
  function productGrid() {
    var all = document.querySelectorAll('[data-section-type="collection-grid"] .grid--uniform, .collection-content .grid--uniform, .grid--uniform');
    for (var i = 0; i < all.length; i++) if (!all[i].closest('rec-row')) return all[i];
    return null;
  }

  // REC-COLLECTION-SORT-1: the product ids on the collection's (dynamically sorted) first page – the row leaves them out
  // (excludeGrid, set by sections/rec-row.liquid when the dynamic order is on), so it never repeats a product near the top.
  function gridIds() {
    var grid = productGrid();
    if (!grid) return [];
    return gridProducts(grid).map(function (el) { return el.getAttribute('data-product-id'); })
      .filter(function (id) { return /^\d+$/.test(id || ''); }).slice(0, 250);
  }

  function columns(grid) {
    var items = gridProducts(grid);
    if (!items.length) return 1;
    var top = items[0].offsetTop, cols = 0;
    for (var i = 0; i < items.length && items[i].offsetTop === top; i++) cols++;
    return Math.max(cols, 1);
  }

  // One standard product card (sections/rec-grid-item.liquid) – on a collection within the collection, so `collection` (custom.plating, links) is the
  // page's; ?variant= = the engine's variant when it chose one (plating). The source or translated handle both work: the
  // page 301s to the visitor's handle and keeps the collection and the query (checked on .de 2026-10-16).
  var cardCache = {};
  // Without a collection (PDP, cart): <locale root>products/<handle>?variant=&section_id=rec-grid-item.
  function gridCard(collectionUrl, tile) {
    var base = collectionUrl ? collectionUrl.replace(/\/$/, '') + '/products/' : root() + 'products/';
    var url = base + encodeURIComponent(tile.handle) + '?' + (tile.exact ? 'variant=' + tile.variantId + '&' : '') + 'section_id=rec-grid-item';
    if (!cardCache[url]) {
      cardCache[url] = fetch(url).then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.text();
      }).then(function (html) {
        var box = document.createElement('div');
        box.innerHTML = html;
        var card = box.querySelector('[data-rec-grid-item] > .grid__item');
        if (!card) throw new Error('no card');
        return card.outerHTML;
      });
      cardCache[url].catch(function () { delete cardCache[url]; });
    }
    return cardCache[url];
  }

  // Preview mock: the collection's products as an engine answer (same fields).
  function mockAnswer(handle, cfg, q) {
    var personal = !!(q.client || q.rv || q.wl);
    var steps = personal ? ['rv', 'sim', 'sim', 'cpop', 'best', 'best'] : ['best'];
    return storefront('collections/' + encodeURIComponent(handle) + '/products.json?limit=24').then(function (data) {
      var rate = window.Shopify && Shopify.currency && parseFloat(Shopify.currency.rate) || 1;
      var products = (data.products || []).filter(function (p) {
        return p.id !== cfg.product && p.variants.some(function (v) {
          return v.available && (!cfg.priceCap || parseFloat(v.price) * 100 <= cfg.priceCap * rate);
        });
      });
      var offset = cfg.surface === 'pdp_complement' ? 6 : cfg.surface === 'cart' ? 12 : 0;
      var picked = products.slice(offset, offset + cfg.limit + 2);
      if (picked.length < cfg.limit) picked = products.slice(0, cfg.limit + 2);
      return {
        v: 1, surface: cfg.surface, market: cfg.market, personal: personal, eligible: personal, holdout: false,
        items: picked.map(function (p, i) {
          var v = p.variants.filter(function (x) { return x.available; })[0];
          var step = cfg.surface === 'pdp_complement' || cfg.surface === 'cart' ? 'bt' : steps[Math.min(i, steps.length - 1)];
          return { slot: i + 1, product_id: p.id, handle: p.handle, variant_id: v.id, step: step };
        })
      };
    });
  }

  class RecRow extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      var json = this.querySelector('[data-rec-config]');
      if (!json) return;
      this.started = true;
      this.cfg = JSON.parse(json.textContent);
      this.t = this.cfg.texts;
      this.added = {};  // slot -> cart line key
      this.token = this.isPage() ? String(this.recToken || '') : '';
      if (!this.cfg.endpoint) {
        if (this.isPage()) this.render(null);
        return;
      }
      this.load();
      if (this.cfg.surface === 'cart') {
        // The cart changed (here or in the drawer): ask again without what is in it now.
        var reload = function () {
          clearTimeout(this.reloadTimer);
          this.reloadTimer = setTimeout(this.load.bind(this), 300);
        }.bind(this);
        ['cart:updated', 'rec:cart-changed', 'ajaxProduct:added', 'cart:build', 'cart:open'].forEach(function (name) {
          document.addEventListener(name, reload);
        });
      }
      // Consent given on this page: the clientId exists now, ask again.
      document.addEventListener('visitorConsentCollected', function () {
        if (this.isPage() && this.dataset.recState === 'rec' && this.out && this.out.personal) return;   // the page keeps its cards
        setTimeout(this.load.bind(this), 500);
      }.bind(this));
    }

    load() {
      var cfg = this.cfg;
      var seq = (this.seq = (this.seq || 0) + 1);
      var withCart = cfg.surface !== 'collection' && cfg.surface !== 'foryou';
      Promise.all([whenClient(), withCart ? cartJSON() : Promise.resolve({ items: [] }), wishlist()]).then(function (res) {
        this.cartProducts = (res[1].items || []).map(function (i) { return i.product_id; });
        var q = {
          surface: cfg.surface,
          market: cfg.market,
          client: res[0],
          product: cfg.product,
          cart: this.cartProducts.filter(function (id, i, a) { return a.indexOf(id) === i; }).slice(0, 50).join(','),
          wl: res[2].join(','),
          rv: visited().join(','),
          limit: cfg.limit,
          po: cfg.personalOnly ? 1 : '',
          collection: cfg.surface === 'collection' && cfg.collection ? cfg.collection : '',
          plating: cfg.surface === 'collection' && cfg.collection && cfg.plating ? cfg.plating : '',
          exclude: cfg.surface === 'collection' && cfg.collection && cfg.excludeGrid ? gridIds().join(',') : '',
          lang: this.isPage() ? cfg.lang : '',
          t: this.token,
          iv: impressionUrl(cfg.endpoint) ? 1 : '',
          wd: automated() ? 1 : ''
        };
        var attempt = function (query) {
          return this.ask(query).then(function (answer) {
            if (this.isPage()) this.dataset.recToken = answer && answer.token ? String(answer.token).replace(/[^a-z]/g, '').slice(0, 10) : (query.t ? 'nil' : 'none');
            return this.resolve(answer);
          }.bind(this));
        }.bind(this);
        // The page: the token's answer, else the same call without the token (visitor's signal / bestsellers), else null.
        if (!q.t) return attempt(q);
        return attempt(q).catch(function () { return null; }).then(function (out) {
          return out || attempt(Object.assign({}, q, { t: '' }));
        });
      }.bind(this)).then(function (out) {
        if (seq !== this.seq) return;
        this.render(out);
      }.bind(this)).catch(function () {
        if (seq === this.seq) this.render(null);
      }.bind(this));
    }

    // The engine's answer, or null.
    ask(q) {
      var cfg = this.cfg;
      if (cfg.endpoint.indexOf('mock:') === 0) return mockAnswer(cfg.endpoint.slice(5), cfg, q).catch(function () { return null; });
      var qs = Object.keys(q).filter(function (k) { return q[k] !== null && q[k] !== undefined && q[k] !== ''; })
        .map(function (k) { return encodeURIComponent(k) + '=' + encodeURIComponent(q[k]); }).join('&');
      return getJSON(cfg.endpoint + (cfg.endpoint.indexOf('?') >= 0 ? '&' : '?') + qs).catch(function () { return null; });
    }

    // Engine items -> tiles with the storefront's data; drops what must not be shown.
    resolve(answer) {
      var cfg = this.cfg;
      if (!answer || !answer.items || !answer.items.length) return null;
      // Holdout is shown only to visitors who would have had a personal row (eligible) – the same
      // population in both groups (REC-FIX-1 G5). An engine without the field keeps the old rule.
      if (cfg.personalOnly && !answer.personal && !(answer.holdout && answer.eligible !== false)) return null;
      var seen = {};
      var items = answer.items.map(function (raw, i) { return readItem(raw, i, cfg.surface, !!answer.holdout); }).filter(function (it) {
        if (!it.handle || seen[it.handle] || (it.productId && it.productId === cfg.product)) return false;
        seen[it.handle] = true;
        return true;
      }).slice(0, cfg.limit + 4);
      var rate = window.Shopify && Shopify.currency && parseFloat(Shopify.currency.rate) || 1;
      var cap = cfg.priceCap > 0 ? cfg.priceCap * rate : 0;
      var inCart = this.cartProducts;
      return Promise.all(items.map(function (it) {
        return productData(it.handle).then(function (p) {
          var v = it.variant && p.variants.filter(function (x) { return x.id === it.variant; })[0];
          var url = p.url;
          if (it.exact) {
            // The variant in the collection's metal: that one or nothing (never another metal).
            if (!v || !v.available) return null;
            url = p.url + (p.url.indexOf('?') >= 0 ? '&' : '?') + 'variant=' + v.id;
          }
          if (!v || !v.available) v = p.variants.filter(function (x) { return x.available; })[0];
          if (!v || !p.available) return null;
          if (p.id === cfg.product) return null;
          if (cfg.surface === 'cart' && inCart.indexOf(p.id) >= 0) return null;
          if (cap && v.price > cap) return null;
          return {
            slot: it.slot, step: it.step, code: it.code, productId: p.id, variantId: v.id, handle: p.handle, exact: it.exact,
            title: p.title, price: v.price, compareAt: v.compare_at_price > v.price ? v.compare_at_price : 0,
            image: v.image || p.image || '', url: url
          };
        }).catch(function () { return null; });
      })).then(function (tiles) {
        tiles = tiles.filter(Boolean).slice(0, cfg.limit);
        if (!tiles.length) return null;
        // Collection row: fewer than 3 left = no row (the engine's rule, REC-COLLECTION-SCOPE-1). The page: cfg.minTiles.
        if (cfg.surface === 'collection' && cfg.collection && tiles.length < 3) return null;
        if (cfg.minTiles && tiles.length < cfg.minTiles) return null;
        var out = { tiles: tiles, holdout: !!answer.holdout, personal: !!answer.personal, iv: typeof answer.iv === 'string' ? answer.iv : '' };
        if (!this.standardCards()) return out;
        // The theme's standard card per tile (TILLÄGG 2/3); a tile whose card does not come is left out. No card at all
        // (e.g. sections/rec-grid-item.liquid not on this theme yet): the row's own cards as before, never in the grid.
        return Promise.all(tiles.map(function (tile) {
          return gridCard(cfg.surface === 'collection' ? cfg.collectionUrl : '', tile).then(function (html) { tile.card = html; return tile; }).catch(function () { return null; });
        })).then(function (withCards) {
          var carded = withCards.filter(Boolean);
          if (!carded.length && !this.inGrid() && !this.isPage()) return out;
          out.tiles = carded;
          out.standard = true;
          if (!carded.length || (cfg.surface === 'collection' && carded.length < 3) || (cfg.minTiles && carded.length < cfg.minTiles)) return null;
          return out;
        }.bind(this));
      }.bind(this));
    }

    // «In grid after row N» with the grid's cards (collection pages; needs the collection's url from the Liquid).
    inGrid() {
      return this.cfg.surface === 'collection' && /^row_\d$/.test(this.cfg.placement || '') && !!this.cfg.collectionUrl;
    }

    // The theme's standard product card (REC-COLLECTION-GRID-ONE-RULE TILLÄGG 3): every 'cards' row (PDP For you,
    // collection above / in the grid) and the cart unless its section keeps the compact add-on list (cardStyle 'compact').
    // «Pairs well with» (discs) keeps its round images (TILLÄGG 4).
    standardCards() {
      var layout = this.dataset.layout;
      return layout === 'cards' || layout === 'page' || (layout === 'addons' && this.cfg.cardStyle !== 'compact');
    }

    // The «For you» page (sections/rec-foryou.liquid).
    isPage() {
      return this.dataset.layout === 'page';
    }

    img(src, width) {
      if (!src) return '';
      var s = String(src).replace(/^http:/, 'https:');
      if (s.indexOf('//') === 0) s = 'https:' + s;
      s = s.replace(/([?&])width=\d+&?/, '$1').replace(/[?&]$/, '');
      return s + (s.indexOf('?') >= 0 ? '&' : '?') + 'width=' + width;
    }

    attrs(tile) {
      return ' data-rec-code="' + esc(tile.code) + '" data-rec-slot="' + esc(tile.slot) + '" data-rec-step="' + esc(tile.step) +
        '" data-rec-product="' + esc(tile.productId) + '" data-rec-variant="' + esc(tile.variantId) + '"';
    }

    render(out) {
      this.out = out;
      this.stopView();
      var config = this.querySelector('[data-rec-config]').outerHTML;
      if (this.isPage()) return this.renderPage(out, config);
      if (!out) {
        this.hidden = true;
        this.innerHTML = config;
        return;
      }
      var html = '';
      if (out.standard) {
        var grid = this.inGrid();
        this.innerHTML = config + (grid ? this.gridHtml(out.tiles) : this.carouselHtml(out.tiles));
        this.dataset.recHoldout = out.holdout ? '1' : '0';
        this.dataset.recPersonal = out.personal ? '1' : '0';
        this.classList.add(grid ? 'rec-row--grid-cards' : 'rec-row--carousel');
        this.hidden = false;
        if (this.cfg.replaceComplementary) this.hideComplementary();
        this.bind();
        if (grid) this.place(); else this.bindArrows();
        this.initCards();
        this.watchView();
        return;
      }
      if (this.t.heading) html += '<h2 class="rec-row__title">' + esc(this.t.heading) + '</h2>';
      if (this.t.subtitle) html += '<p class="rec-row__subtitle">' + esc(this.t.subtitle) + '</p>';
      if (this.dataset.layout === 'discs') html += this.discs(out.tiles);
      else if (this.dataset.layout === 'addons') html += this.addons(out.tiles);
      else html += this.cards(out.tiles);
      html += '<p class="rec-row__status" data-rec-status role="status" aria-live="polite"></p>';
      this.innerHTML = config + html;
      this.dataset.recHoldout = out.holdout ? '1' : '0';
      this.dataset.recPersonal = out.personal ? '1' : '0';
      this.hidden = false;
      if (this.cfg.replaceComplementary) this.hideComplementary();
      this.bind();
      if (this.dataset.layout === 'discs') this.bindArrows();
      this.place();
      this.watchView();
    }

    // REC-BOTGUARD-1: the ticket goes to the engine once, when the row has really been seen (see the header).
    watchView() {
      var out = this.out, url = impressionUrl(this.cfg.endpoint);
      if (!out || !out.iv || !url || automated() || !window.IntersectionObserver) return;
      var self = this, since = 0, seen = false;
      var steps = [];
      for (var i = 0; i <= 20; i++) steps.push(i / 20);
      var arm = function () {
        if (self.viewTimer || !seen || document.hidden) return;
        since = Date.now();
        self.viewTimer = setTimeout(function () {
          self.viewTimer = null;
          if (seen && !document.hidden && self.out === out) self.sendView(out, url, Date.now() - since);
        }, VIEW_MS);
      };
      var disarm = function () {
        clearTimeout(self.viewTimer);
        self.viewTimer = null;
      };
      this.viewObserver = new IntersectionObserver(function (entries) {
        var e = entries[entries.length - 1];
        var h = e.boundingClientRect.height, vh = window.innerHeight || (e.rootBounds && e.rootBounds.height) || h;
        seen = e.isIntersecting && e.intersectionRect.width > 0 && h > 0 && e.intersectionRect.height >= VIEW_SHARE * Math.min(h, vh) - 1;
        if (seen) arm(); else disarm();
      }, { threshold: steps });
      this.viewObserver.observe(this);
      this.viewVisibility = function () { if (document.hidden) disarm(); else arm(); };
      document.addEventListener('visibilitychange', this.viewVisibility);
    }

    stopView() {
      clearTimeout(this.viewTimer);
      this.viewTimer = null;
      if (this.viewObserver) this.viewObserver.disconnect();
      this.viewObserver = null;
      if (this.viewVisibility) document.removeEventListener('visibilitychange', this.viewVisibility);
      this.viewVisibility = null;
    }

    sendView(out, url, ms) {
      if (!out.iv || automated()) return;
      var seenSlots = {}, slots = [], codes = [];
      Array.prototype.forEach.call(this.querySelectorAll('[data-rec-slot]'), function (el) {
        if (el.hidden || el.closest('[hidden]') || !el.getClientRects().length) return;
        var slot = Number(el.dataset.recSlot);
        if (!slot || seenSlots[slot]) return;
        seenSlots[slot] = true;
        slots.push(slot);
        codes.push(el.dataset.recCode || '');
      });
      var iv = out.iv;
      out.iv = '';                       // once per answer
      this.stopView();
      if (!slots.length) return;
      sendBeacon(url, { iv: iv, slots: slots, view: 1, ms: Math.round(ms), wd: 0 });
      var detail = { surface: this.cfg.surface, codes: codes };
      publish('timi:rec_view', detail);
      try { this.dispatchEvent(new CustomEvent('timi:rec_view', { bubbles: true, detail: detail })); } catch (e) {}
    }

    // The page: the engine's standard cards in a full grid, or – nothing (enough) from the engine – the section's fallback
    // collection cards (drawn in Liquid, without data-rec-*: not counted as rec clicks). Never hidden.
    renderPage(out, config) {
      if (!out || !out.standard) {
        this.dataset.recState = 'fallback';
        this.hidden = false;
        return;
      }
      this.innerHTML = config + '<div class="grid grid--uniform rec-row__page-grid" data-rec-track>' + this.cardsHtml(out.tiles, null) + '</div>';
      this.dataset.recHoldout = out.holdout ? '1' : '0';
      this.dataset.recPersonal = out.personal ? '1' : '0';
      this.dataset.recState = 'rec';
      this.hidden = false;
      this.bind();
      this.bindQuickAdd();
      this.initCards();
      this.watchView();
    }

    // A Quick view add from a rec card (the standard card has no add button of its own): the card that opened Quick view is
    // remembered, and the theme's ajaxProduct:added for that product publishes timi:rec_add (REC-COLLECT). The cart line's
    // _rec property is not set – the form is the theme's.
    bindQuickAdd() {
      Array.prototype.forEach.call(this.querySelectorAll('.quick-product__btn'), function (btn) {
        btn.addEventListener('click', function () { this.quickTile = this.tileOf(btn); }.bind(this));
      }.bind(this));
      if (this.quickBound) return;
      this.quickBound = true;
      document.addEventListener('ajaxProduct:added', function (evt) {
        var tile = this.quickTile, item = evt.detail && evt.detail.product;
        if (!tile || !item || Number(item.product_id) !== Number(tile.productId)) return;
        this.quickTile = null;
        publish('timi:rec_add', { code: tile.code, product_id: tile.productId, variant_id: Number(item.variant_id || item.id) || tile.variantId });
      }.bind(this));
    }

    // Collection pages: placement 'row_N' moves the row into the product grid after N rows
    // (TILLÄGG 3). The grid is Impulse's float grid (.grid--uniform, clearing by :nth-of-type on
    // the product divs) – this element is <rec-row>, not a div, so the products' counting and
    // clearing are untouched; it takes the full width (grid__item + one-whole + clear).
    place() {
      var m = /^row_(\d)$/.exec(this.cfg.placement || '');
      if (!m || this.cfg.surface !== 'collection' || this.hidden) return;
      var grid = this.gridEl || productGrid();
      if (!grid) return;
      this.gridEl = grid;
      var at = placementIndex(grid, Number(m[1]));
      var items = gridProducts(grid);
      this.classList.add('grid__item', 'rec-row--in-grid');
      var ref = at < items.length ? items[at] : null;
      if (ref ? ref.previousElementSibling !== this : grid.lastElementChild !== this) grid.insertBefore(this, ref);
      this.oneLine(grid);
      if (!this.placeBound) {
        this.placeBound = true;
        var again = function () { clearTimeout(this.placeTimer); this.placeTimer = setTimeout(this.place.bind(this), 150); }.bind(this);
        window.addEventListener('resize', again);
      }
    }

    // Grid cards: the grid's heading typography (section-header) and its own cards, with the grid's item classes.
    // Heading as the theme's «You may also like» (sections/product-recommendations.liquid: section-header +
    // h3.section-header__title, capitalised by the theme setting), subtitle under it.
    headHtml() {
      if (!this.t.heading && !this.t.subtitle) return '';
      return '<header class="section-header rec-row__grid-header">' +
        (this.t.heading ? '<h3 class="section-header__title">' + esc(this.t.heading) + '</h3>' : '') +
        (this.t.subtitle ? '<p class="rec-row__subtitle">' + esc(this.t.subtitle) + '</p>' : '') + '</header>';
    }

    // The standard cards with the row's data on them (data-rec-*, click measuring); classes = the grid's when given.
    cardsHtml(tiles, first) {
      var box = document.createElement('div');
      box.innerHTML = tiles.map(function (t) { return t.card; }).join('');
      Array.prototype.forEach.call(box.children, function (card, i) {
        var tile = tiles[i];
        if (first) {
          // per_row is the grid block's setting: take the grid's own item classes and animation attribute.
          card.className = first.className.replace(/(^|\s)aos-animate(?=\s|$)/g, ' ').replace(/\s+/g, ' ').trim();
          if (first.hasAttribute('data-aos')) card.setAttribute('data-aos', first.getAttribute('data-aos'));
        }
        card.setAttribute('data-rec-code', tile.code);
        card.setAttribute('data-rec-slot', tile.slot);
        card.setAttribute('data-rec-step', tile.step);
        card.setAttribute('data-rec-product', tile.productId);
        card.setAttribute('data-rec-variant', tile.variantId);
        Array.prototype.forEach.call(card.querySelectorAll('a[href]'), function (a) { a.setAttribute('data-rec-link', ''); });
      });
      return box.innerHTML;
    }

    // Sideways carousel (TILLÄGG 3): 4 cards visible on desktop, 2 on mobile (the cart: 2), more behind; swipe, and
    // arrows on desktop. Native scrolling (scroll-snap) – no transform, so the cards' Quick view modal (position: fixed)
    // still covers the page.
    carouselHtml(tiles) {
      return this.headHtml() + '<div class="rec-row__carousel-wrap">' + this.arrow('prev') +
        '<div class="grid grid--uniform rec-row__carousel" data-rec-track>' + this.cardsHtml(tiles, null) + '</div>' +
        this.arrow('next') + '</div>';
    }

    // The theme's chevron (icon-chevron-left, as in its sliders); 'next' is the same icon turned by CSS.
    arrow(dir) {
      var chev = '<svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-chevron-left" viewBox="0 0 284.49 498.98"><path d="M249.49 0a35 35 0 0 1 24.75 59.75L84.49 249.49l189.75 189.74a35.002 35.002 0 1 1-49.5 49.5L10.25 274.24a35 35 0 0 1 0-49.5L224.74 10.25A34.89 34.89 0 0 1 249.49 0Z"/></svg>';
      return '<button type="button" class="rec-row__arrow rec-row__arrow--' + dir + '" data-rec-' + dir + ' aria-label="' +
        esc((dir === 'prev' ? this.t.prev : this.t.next) || (dir === 'prev' ? 'Previous' : 'Next')) + '"' + (dir === 'prev' ? ' disabled' : '') + '>' + chev + '</button>';
    }

    bindArrows() {
      var track = this.querySelector('[data-rec-track]');
      var prev = this.querySelector('[data-rec-prev]'), next = this.querySelector('[data-rec-next]');
      if (!track) return;
      var step = function (dir) {
        var card = track.firstElementChild;
        var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        var w = card ? card.getBoundingClientRect().width + gap : track.clientWidth / 4;
        var per = Math.max(1, Math.round(track.clientWidth / w));
        track.scrollBy({ left: dir * per * w, behavior: 'smooth' });
      };
      var state = function () {
        prev.disabled = track.scrollLeft <= 2;
        next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
        this.classList.toggle('rec-row--scrolls', track.scrollWidth > track.clientWidth + 2);
      }.bind(this);
      prev.addEventListener('click', function () { step(-1); });
      next.addEventListener('click', function () { step(1); });
      track.addEventListener('scroll', function () { clearTimeout(this.arrowTimer); this.arrowTimer = setTimeout(state, 60); }.bind(this), { passive: true });
      window.addEventListener('resize', state);
      state();
    }

    gridHtml(tiles) {
      var head = this.headHtml();
      var first = gridProducts(this.gridEl || productGrid() || document.createElement('div'))[0];
      return head + '<div class="grid grid--uniform rec-row__grid">' + this.cardsHtml(tiles, first) + '</div>';
    }

    // Exactly one line of cards: as many as the grid has columns now.
    oneLine(grid) {
      if (!this.classList.contains('rec-row--grid-cards')) return;
      var cols = columns(grid);
      Array.prototype.forEach.call(this.querySelectorAll('.rec-row__grid > .grid__item'), function (card, i) {
        card.hidden = i >= cols;
      });
    }

    // What the theme does after it draws grid cards itself (product recommendations): AOS, quick shop, collapsibles –
    // theme.reinitProductGridItem – plus the collection's swatch hover (theme.Collection.colorSwatchHovering) for these
    // cards. The wishlist app adds its heart by itself (MutationObserver on its card selector).
    initCards() {
      try {
        if (window.theme && typeof theme.reinitProductGridItem === 'function') theme.reinitProductGridItem(this);
        else if (window.AOS) AOS.refreshHard();
      } catch (e) { /* the cards still show */ }
      var scope = this;
      Array.prototype.forEach.call(this.querySelectorAll('.color-swatch--with-image'), function (swatch) {
        var target = function () { return scope.querySelector('.grid-product__color-image--' + swatch.dataset.variantId); };
        swatch.addEventListener('mouseenter', function () {
          var el = target();
          if (!el) return;
          el.style.backgroundImage = 'url(' + swatch.dataset.variantImage + ')';
          el.classList.add('is-active');
        });
        swatch.addEventListener('mouseleave', function () {
          var el = target();
          if (el) el.classList.remove('is-active');
        });
      });
    }

    hideComplementary() {
      var scope = this.closest('.product-section, [data-section-type]') || document;
      Array.prototype.forEach.call(scope.querySelectorAll('product-recommendations[data-intent="complementary"]'), function (el) {
        var block = el.closest('.product-block') || el;
        block.hidden = true;
        block.setAttribute('data-rec-replaced', '');
      });
    }

    cards(tiles) {
      return '<ul class="rec-row__cards" role="list">' + tiles.map(function (tile) {
        return '<li class="rec-row__card"' + this.attrs(tile) + '>' +
          '<a class="rec-row__card-link" href="' + esc(tile.url) + '" data-rec-link>' +
          '<span class="rec-row__card-img">' + (tile.image ? '<img src="' + esc(this.img(tile.image, 360)) + '" alt="" loading="lazy" width="360" height="360">' : '') + '</span>' +
          '<span class="rec-row__card-title">' + esc(tile.title) + '</span>' +
          '<span class="rec-row__card-price">' + (tile.compareAt ? '<s>' + esc(money(tile.compareAt)) + '</s> ' : '') + esc(money(tile.price)) + '</span>' +
          '</a></li>';
      }.bind(this)).join('') + '</ul>';
    }

    // Same look as the charm row (charm-row.css): round image, black plus, price on hover.
    // TILLÄGG 4: a sideways row (more behind, swipe / arrows on desktop), same disc width as before; a smaller plus.
    discs(tiles) {
      return '<div class="rec-row__carousel-wrap">' + this.arrow('prev') +
        '<ul class="charm-row__grid rec-row__discs" role="list" data-rec-track>' + tiles.map(function (tile) {
        return '<li class="charm-row__item"' + this.attrs(tile) + '>' +
          '<button type="button" class="charm-row__charm" data-rec-add aria-pressed="false" aria-label="' + esc(fill(this.t.add, tile.title)) + '">' +
          '<span class="charm-row__disc">' + (tile.image ? '<img class="charm-row__img" src="' + esc(this.img(tile.image, 240)) + '" alt="" loading="lazy" width="96" height="96">' : '') + '</span>' +
          '<span class="charm-row__plus" aria-hidden="true">' +
          '<svg class="charm-row__icon-plus" viewBox="0 0 20 20" width="14" height="14"><path d="M10 3v14M3 10h14" stroke="currentColor" stroke-width="1.6" fill="none"/></svg>' +
          '<svg class="charm-row__icon-check" viewBox="0 0 20 20" width="14" height="14"><path d="M4 10.5l4 4 8-9" stroke="currentColor" stroke-width="1.8" fill="none"/></svg>' +
          '</span>' +
          '<span class="charm-row__meta"><span class="charm-row__price">' + esc(money(tile.price)) + '</span></span>' +
          '</button>' +
          '<a class="rec-row__disc-link" href="' + esc(tile.url) + '" data-rec-link>' + esc(tile.title) + '</a>' +
          '</li>';
      }.bind(this)).join('') + '</ul>' + this.arrow('next') + '</div>';
    }

    addons(tiles) {
      return '<ul class="rec-row__addons" role="list">' + tiles.map(function (tile) {
        return '<li class="rec-row__addon"' + this.attrs(tile) + '>' +
          '<a class="rec-row__addon-img" href="' + esc(tile.url) + '" data-rec-link tabindex="-1" aria-hidden="true">' + (tile.image ? '<img src="' + esc(this.img(tile.image, 120)) + '" alt="" loading="lazy" width="56" height="56">' : '') + '</a>' +
          '<span class="rec-row__addon-text"><a href="' + esc(tile.url) + '" data-rec-link class="rec-row__addon-title">' + esc(tile.title) + '</a>' +
          '<span class="rec-row__addon-price">' + esc(money(tile.price)) + '</span></span>' +
          '<button type="button" class="btn btn--small rec-row__addon-btn" data-rec-add aria-label="' + esc(fill(this.t.add, tile.title)) + '">' + esc(this.t.addButton) + '</button>' +
          '</li>';
      }.bind(this)).join('') + '</ul>';
    }

    tileOf(el) {
      var slot = Number(el.closest('[data-rec-slot]').dataset.recSlot);
      return this.out.tiles.filter(function (t) { return t.slot === slot; })[0];
    }

    bind() {
      Array.prototype.forEach.call(this.querySelectorAll('[data-rec-link]'), function (a) {
        a.addEventListener('click', function () {
          var tile = this.tileOf(a);
          publish('timi:rec_click', { code: tile.code, product_id: tile.productId });
        }.bind(this));
      }.bind(this));
      Array.prototype.forEach.call(this.querySelectorAll('[data-rec-add]'), function (btn) {
        btn.addEventListener('click', this.onAdd.bind(this, btn));
      }.bind(this));
    }

    onAdd(btn) {
      var tile = this.tileOf(btn);
      if (btn.classList.contains('is-busy')) return;
      btn.classList.add('is-busy');
      var status = this.querySelector('[data-rec-status]');
      var key = this.added[tile.slot];
      var request = key
        ? postJSON('cart/change.js', { id: key, quantity: 0 }).then(function () { return null; })
        : postJSON('cart/add.js', { items: [{ id: tile.variantId, quantity: 1, properties: { _rec: tile.code } }] })
          .then(function (data) { return data.items && data.items[0] ? data.items[0].key : null; });
      request.then(function (newKey) {
        if (newKey) {
          this.added[tile.slot] = newKey;
          publish('timi:rec_add', { code: tile.code, product_id: tile.productId, variant_id: tile.variantId });
        } else {
          delete this.added[tile.slot];
        }
        if (btn.hasAttribute('aria-pressed')) {
          btn.setAttribute('aria-pressed', newKey ? 'true' : 'false');
          btn.setAttribute('aria-label', fill(newKey ? this.t.remove : this.t.add, tile.title));
        }
        status.textContent = newKey ? fill(this.t.added, tile.title) : '';
        // The theme rebuilds the drawer / cart page (lines, subtotal, count).
        document.dispatchEvent(new CustomEvent('cart:build'));
        document.dispatchEvent(new CustomEvent('rec:cart-changed'));
      }.bind(this)).catch(function () {
        status.textContent = this.t.error;
      }.bind(this)).finally(function () {
        btn.classList.remove('is-busy');
      });
    }
  }

  window.customElements.define('rec-row', RecRow);

  // Cart add-ons: one row of texts (sections/rec-cart.liquid) drawn into the cart
  // drawer and the cart page (data-rec-cart-slot).
  function fillCartSlots() {
    var tpl = document.getElementById('RecCartTemplate');
    if (!tpl) return;
    Array.prototype.forEach.call(document.querySelectorAll('[data-rec-cart-slot]'), function (slot, i) {
      if (slot.firstElementChild) return;
      var node = tpl.content.cloneNode(true);
      var row = node.querySelector('rec-row');
      if (row) row.id = 'RecCart-' + (slot.dataset.recCartSlot || i);
      slot.appendChild(node);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fillCartSlots);
  else fillCartSlots();
})();
